import axios from 'axios';
import { secureApi } from './secureApi';

const API_URL = process.env.REACT_APP_API_BASE_URL
  ? `${process.env.REACT_APP_API_BASE_URL}/api`
  : '/api';

// Get auth header from localStorage
const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { 'x-auth-token': token } : {};
};

const taskApi = {
  /**
   * Get list of tasks with optional filters
   * @param {Object} filters - Filter parameters
   * @returns {Promise} Response with tasks array and pagination
   */
  getTasks: async (filters = {}) => {
    const response = await axios.get(`${API_URL}/tasks`, {
      headers: getAuthHeader(),
      params: filters
    });
    return response.data;
  },

  /**
   * Server-provided defaults for the create-task form (business date, Riyadh).
   * Never compute these in the browser — see TEMPORAL_FIELD_CATALOGUE.md.
   * @returns {Promise} Response with data.defaults
   */
  getFormDefaults: async () => {
    const response = await axios.get(`${API_URL}/tasks/form-defaults`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  /**
   * Get single task by ID
   * @param {String} id - Task ID
   * @returns {Promise} Response with task object
   */
  getTask: async (id) => {
    const response = await axios.get(`${API_URL}/tasks/${id}`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  /**
   * Get today's tasks grouped by priority
   * @returns {Promise} Response with grouped tasks
   */
  getDailyTasks: async () => {
    const response = await axios.get(`${API_URL}/tasks/daily-list`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  /**
   * Get tasks for specific property
   * @param {String} propertyId - Property ID
   * @param {Number} days - Number of days ahead (default 7)
   * @returns {Promise} Response with tasks for property
   */
  getPropertyTasks: async (propertyId, days = 7) => {
    const response = await axios.get(`${API_URL}/tasks/property/${propertyId}`, {
      headers: getAuthHeader(),
      params: { days }
    });
    return response.data;
  },

  /**
   * Create a new manual task
   * @param {Object} taskData - Task data
   * @returns {Promise} Response with created task
   */
  createTask: async (taskData) => {
    const response = await axios.post(`${API_URL}/tasks`, taskData, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  /**
   * Update task status
   * @param {String} id - Task ID
   * @param {String} status - New status
   * @param {Object} options - Optional: { notes, completionNotes, completionRating, guestAssessment }
   * @returns {Promise} Response with updated task
   */
  updateTaskStatus: async (id, status, options = {}) => {
    const body = { status };
    if (options.notes) body.notes = options.notes;
    if (options.completionNotes) body.completionNotes = options.completionNotes;
    if (options.completionRating) body.completionRating = options.completionRating;
    // "Would you host this guest again?" on a turnover clean — validated by the server
    if (options.guestAssessment) body.guestAssessment = options.guestAssessment;

    const response = await axios.patch(
      `${API_URL}/tasks/${id}/status`,
      body,
      { headers: getAuthHeader() }
    );
    return response.data;
  },

  /**
   * Assign task to team member
   * @param {String} id - Task ID
   * @param {String} assignedTo - User ObjectId (or null to unassign)
   * @param {String} notes - Optional notes
   * @returns {Promise} Response with updated task
   */
  assignTask: async (id, assignedTo, notes = null) => {
    const response = await axios.patch(
      `${API_URL}/tasks/${id}/assign`,
      { assignedTo, notes },
      { headers: getAuthHeader() }
    );
    return response.data;
  },

  /**
   * Update task details
   * @param {String} id - Task ID
   * @param {Object} updates - Fields to update
   * @returns {Promise} Response with updated task
   */
  updateTask: async (id, updates) => {
    const response = await axios.patch(
      `${API_URL}/tasks/${id}`,
      updates,
      { headers: getAuthHeader() }
    );
    return response.data;
  },

  /**
   * «انضممت» — a partner joins a clean under way (tasks/cleaning-performance-report.md D3/D4).
   * On the shared `secureApi` instance, not this file's legacy raw axios: new calls use the shared
   * instance (CLAUDE.md). Its refusals (TASK_008 / TASK_009) are 409s, which keep their `code`.
   * @param {String} id - Task ID
   * @returns {Promise} { task, joined } — `joined` is false when the caller was already on it
   */
  joinClean: async (id) => {
    const response = await secureApi.post(`/api/tasks/${id}/join`);
    return response.data;
  },

  /**
   * A clean's record for a supervisor to correct (tasks/cleaning-performance-report.md D7):
   * start, who took part, crew limit, and the corrections already filed. TASK_MANAGERS only.
   * @param {String} id - Task ID
   * @returns {Promise} { startedAt, completedAt, cleaners[], crewLimit, corrections[] }
   */
  getCleanRecord: async (id) => {
    const response = await secureApi.get(`/api/tasks/${id}/clean-record`);
    return response.data;
  },

  /**
   * Correct a clean's record — send only what changes, always with a reason. The original is kept
   * by the server. Refusals are 400 TASK_006 + details.field, or 409 TASK_008.
   * @param {String} id - Task ID
   * @param {Object} change - { startedAt?: ISO, cleanerUserIds?: [id], reason }
   * @returns {Promise} the record as `getCleanRecord` returns it
   */
  correctCleanRecord: async (id, change) => {
    const response = await secureApi.patch(`/api/tasks/${id}/clean-record`, change);
    return response.data;
  },

  /**
   * Cancel/delete task
   * @param {String} id - Task ID
   * @returns {Promise} Response confirming cancellation
   */
  deleteTask: async (id) => {
    const response = await axios.delete(`${API_URL}/tasks/${id}`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  /**
   * Upload a photo to a task
   * @param {String} taskId - Task ID
   * @param {File} file - Image file (JPEG, PNG, WebP)
   * @param {String} type - Photo type: 'before' | 'after' | 'issue' | 'completion'
   * @param {String} caption - Optional caption
   * @returns {Promise} Response with photo object
   */
  uploadTaskPhoto: async (taskId, file, type = 'completion', caption = '') => {
    const formData = new FormData();
    formData.append('photo', file);
    formData.append('type', type);
    if (caption) formData.append('caption', caption);

    const response = await axios.post(
      `${API_URL}/tasks/${taskId}/photos`,
      formData,
      {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'multipart/form-data'
        }
      }
    );
    return response.data;
  },

  /**
   * Delete a photo from a task
   * @param {String} taskId - Task ID
   * @param {String} photoId - Photo subdocument ID
   * @returns {Promise} Response confirming deletion
   */
  deleteTaskPhoto: async (taskId, photoId) => {
    const response = await axios.delete(
      `${API_URL}/tasks/${taskId}/photos/${photoId}`,
      { headers: getAuthHeader() }
    );
    return response.data;
  }
};

export default taskApi;
